import { useRef, useState, useEffect } from 'react';
import { ZodSchema } from 'zod';
import {
  errorsProps,
  ValidationErrorTypes,
  warningsProps,
} from '../components/SummaryErrors/SummaryErrors';
import { formatDatesInErrorMessage } from '../utils/dateFormat';
import { BackendValidationIssue } from '../apiClient/apiClient';
import {
  getValidationIssueErrors,
  getValidationIssueWarnings,
} from '../utils/backendValidationIssues';

export type ValidationError = BackendValidationIssue;

export const getBackendValidationErrors = (
  error: unknown
): ValidationError[] => {
  return getValidationIssueErrors(error);
};

const issuesAreEqual = (
  a: (errorsProps | warningsProps)[],
  b: (errorsProps | warningsProps)[]
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  return JSON.stringify(a) === JSON.stringify(b);
};

type BackendValidationProps<T extends ZodSchema> = {
  validate?: (formData: T) => Promise<unknown>;
  formData: T;
  formDataIsDirty: boolean;
  fieldLabels: Record<string, string>;
  frontendValidationPassed: boolean;
  validationKey?: string | number;
  initialErrors?: errorsProps[];
  initialWarnings?: warningsProps[];
  onValidationComplete?: (formData: T) => void;
};

const mapWarningIssuesToWarnings = (
  warningIssues: BackendValidationIssue[],
  fieldLabels: Record<string, string>
): warningsProps[] => {
  return warningIssues.map((issue) => {
    const field = issue.affectedFields[0] ?? '';
    const message = formatDatesInErrorMessage(issue.message);
    return {
      fieldName: field,
      feature: issue.feature,
      fieldWarnings: [message],
      fieldLabel: fieldLabels[field] || field,
      type: ValidationErrorTypes.Backend,
      affectedFields: issue.affectedFields.map((f) => ({
        fieldName: f,
        fieldLabel: fieldLabels[f] || f,
      })),
    };
  });
};

const mapValidationErrorsToErrors = (
  validationErrors: ValidationError[],
  fieldLabels: Record<string, string>
): errorsProps[] => {
  return validationErrors.map((error) => {
    const field = error.affectedFields[0] ?? '';
    const message = formatDatesInErrorMessage(error.message);
    return {
      fieldName: field,
      feature: error.feature,
      fieldErrors: [message],
      fieldLabel: fieldLabels[field] || field,
      type: ValidationErrorTypes.Backend,
      affectedFields: error.affectedFields.map((f) => ({
        fieldName: f,
        fieldLabel: fieldLabels[f] || f,
      })),
    };
  });
};

export function useBackendValidation<T extends ZodSchema>({
  validate,
  formData,
  formDataIsDirty,
  fieldLabels,
  frontendValidationPassed,
  validationKey,
  initialErrors,
  initialWarnings,
  onValidationComplete,
}: BackendValidationProps<T>) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const requestGenerationRef = useRef(0);
  const prevValidationKeyRef = useRef(validationKey);
  const isFirstRunRef = useRef(true);
  const [validationErrors, setValidationErrors] = useState<errorsProps[]>(
    initialErrors ?? []
  );
  const [isValid, setIsValid] = useState(true);
  const [isValidating, setIsValidatingState] = useState(false);
  const [hasValidatedCurrentForm, setHasValidatedCurrentForm] = useState(
    initialErrors?.length === 0 && initialWarnings?.length === 0
  );
  const isValidatingRef = useRef(false);

  const setIsValidating = (val: boolean) => {
    isValidatingRef.current = val;
    setIsValidatingState(val);
  };

  const [warnings, setWarnings] = useState<warningsProps[]>(
    initialWarnings ?? []
  );

  if (
    validationKey !== undefined &&
    validationKey !== prevValidationKeyRef.current
  ) {
    prevValidationKeyRef.current = validationKey;
    requestGenerationRef.current += 1;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const cached = initialErrors ?? [];
    setValidationErrors(cached);
    setWarnings(initialWarnings ?? []);
    setIsValid(cached.length === 0);
    setIsValidating(false);
    setHasValidatedCurrentForm(false);
  }

  useEffect(() => {
    if (isFirstRunRef.current) return;
    const next = initialErrors ?? [];
    setValidationErrors((prev) => {
      if (issuesAreEqual(prev, next)) return prev;
      return next;
    });
    setIsValid(next.length === 0);
  }, [JSON.stringify(initialErrors)]);

  useEffect(() => {
    if (isFirstRunRef.current) return;
    const next = initialWarnings ?? [];
    setWarnings((prev) => {
      if (issuesAreEqual(prev, next)) return prev;
      return next;
    });
  }, [JSON.stringify(initialWarnings)]);

  const triggerValidation = (dataToValidate?: T) => {
    const data = dataToValidate ?? formData;
    requestGenerationRef.current += 1;
    const generation = requestGenerationRef.current;

    setIsValidating(true);
    setHasValidatedCurrentForm(false);
    validateRef.current!(data)
      .then((response) => {
        if (generation !== requestGenerationRef.current) return;
        const warningIssues = getValidationIssueWarnings(response) || [];
        const warnings = mapWarningIssuesToWarnings(warningIssues, fieldLabels);
        setWarnings((prev) =>
          issuesAreEqual(prev, warnings) ? prev : warnings
        );
        setValidationErrors((prev) => (prev.length === 0 ? prev : []));
        setIsValid(true);
        setHasValidatedCurrentForm(true);
        onValidationComplete?.(data);
      })
      .catch((e: unknown) => {
        if (generation !== requestGenerationRef.current) return;

        const warningIssues = getValidationIssueWarnings(e);
        const warnings = mapWarningIssuesToWarnings(warningIssues, fieldLabels);
        setWarnings((prev) =>
          issuesAreEqual(prev, warnings) ? prev : warnings
        );

        const validationErrors = getBackendValidationErrors(e);
        if (validationErrors.length === 0) {
          setHasValidatedCurrentForm(true);
          if (warningIssues.length > 0) {
            setIsValid(true);
          }
          return;
        }

        const errors = mapValidationErrorsToErrors(
          validationErrors,
          fieldLabels
        );
        setValidationErrors((prev) =>
          issuesAreEqual(prev, errors) ? prev : errors
        );
        setIsValid(false);
        setHasValidatedCurrentForm(true);
        onValidationComplete?.(data);
      })
      .finally(() => {
        if (generation !== requestGenerationRef.current) return;
        setIsValidating(false);
      });
  };

  useEffect(() => {
    if (!validateRef.current) {
      return;
    }

    if (!frontendValidationPassed) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      requestGenerationRef.current += 1;
      isFirstRunRef.current = false;
      setHasValidatedCurrentForm(false);
      return;
    }

    if (!formDataIsDirty) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      requestGenerationRef.current += 1;
      if (!isFirstRunRef.current || !initialErrors?.length) {
        setValidationErrors((prev) => (prev.length === 0 ? prev : []));
        setWarnings((prev) => (prev.length === 0 ? prev : []));
      }
      isFirstRunRef.current = false;
      setHasValidatedCurrentForm(false);
      return;
    }

    isFirstRunRef.current = false;
    setIsValidating(true);
    setHasValidatedCurrentForm(false);
    timeoutRef.current = setTimeout(() => triggerValidation(formData), 250);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      requestGenerationRef.current += 1;
    };
  }, [JSON.stringify(formData), formDataIsDirty]);

  return {
    validationErrors,
    warnings,
    isValid,
    isValidating,
    isValidatingRef,
    hasValidatedCurrentForm,
    triggerBackendValidation: triggerValidation,
  };
}
